import { MEMBERS_AREA_V2 } from '@wix/app-definition-ids';
import {
  type EditorReadyFn,
  type EditorReadyOptions,
  type EditorSDK,
  EditorSubType,
  EditorType,
} from '@wix/platform-editor-sdk';

import { shouldEnableV3TpaPageLinksDataFixer, shouldInstallIdentityAuthAppForMaV3 } from '../../../utils/experiments';
import { log, monitoredTransactionFactory, toMonitored } from '../../../utils/monitoring';
import { APP_TOKEN } from '../../constants';
import { maybeFixBrokenMenusData } from '../../data-fixers/fixBrokenMenus';
import { startSequentialPromises, stopSequentialPromises } from '../../enforceSequentiality';
import { isApplicationInstalled } from '../../wrappers/application';
import { registerToComponentAddedToStageEvent } from '../../wrappers/components';
import { add as addHistory, HistoryLabel } from '../../wrappers/history';
import { fixTpaPageLinksIfCorrupted } from './data-fixers/fix-tpa-page-links';
import { installMembersArea } from './editor-ready-utils/installation';
import { monitoredMaybeInstallIdentityAuthApp } from './editor-ready-utils/monitored-methods';
import { addEventsListeners } from './event-listener';
import { registerAlwaysAvailableApps } from './integration';
import { signalBlocksPlatformFailed, signalBlocksPlatformReady } from './services/blocks-platform-ready';
import { removeGlobalControllerIfExists } from './services/global-controller';
import { setStateForPages } from './services/pages';

const shouldInstallMembersArea = async (editorSDK: EditorSDK, options: EditorReadyOptions) => {
  if (options.firstInstall) {
    const isBobApplicationInstalled = await isApplicationInstalled(editorSDK, MEMBERS_AREA_V2);
    return !isBobApplicationInstalled;
  }

  return false;
};

const setSignUpPrivacyNoteType = async (editorSDK: EditorSDK) => {
  try {
    await editorSDK.siteMembers.setPrivacyNoteType(APP_TOKEN, 'NOTE');
  } catch {
    log('Failed to set privacyNoteType');
  }
};

export const editorReady: EditorReadyFn = async (editorSDK, _, options) => {
  const monitoredTransaction = monitoredTransactionFactory(editorSDK);

  const editorReadyAction = async () => {
    if (await shouldInstallMembersArea(editorSDK, options)) {
      const enableIdentityAuthAppInstall = await shouldInstallIdentityAuthAppForMaV3();

      await removeGlobalControllerIfExists(editorSDK);
      await monitoredTransaction('v3.install', async () => {
        const editorType = (
          options.origin.subType ?? (options.origin.type === EditorType.Classic ? EditorSubType.Classic : undefined)
        )?.toLowerCase();
        const installMembersAreaInteractionName = editorType
          ? `v3.install.membersArea.${editorType}`
          : 'v3.install.membersArea';

        return Promise.all([
          toMonitored(installMembersAreaInteractionName, () => installMembersArea(editorSDK, options)),
          enableIdentityAuthAppInstall ? monitoredMaybeInstallIdentityAuthApp(editorSDK) : Promise.resolve(),
        ]);
      });
      await setSignUpPrivacyNoteType(editorSDK);
      await addHistory(editorSDK, HistoryLabel.InstallV3);
    }

    signalBlocksPlatformReady();

    setStateForPages(editorSDK);
    await registerToComponentAddedToStageEvent(editorSDK);
    await registerAlwaysAvailableApps(editorSDK);
    await addEventsListeners(editorSDK);

    // Try to fix broken menus data
    // TODO: merge data fixers - https://jira.wixpress.com/browse/MA-3358
    await maybeFixBrokenMenusData(editorSDK);

    if (await shouldEnableV3TpaPageLinksDataFixer()) {
      await fixTpaPageLinksIfCorrupted(editorSDK);
    }

    startSequentialPromises();
  };

  try {
    await toMonitored('v3.editorReady', editorReadyAction);
  } catch (error: any) {
    console.error('Members Area V3 editorReady failed');
    console.error(error);
    signalBlocksPlatformFailed(error);
    stopSequentialPromises(error);

    throw error;
  }
};
